/**
 * GOYOUTATI 參考價格顯示腳本
 * 適用主題：Dawn
 * 功能：根據客戶選擇的國家，在日幣價格旁顯示當地貨幣參考價格
 * 
 * 安裝方式：
 * 1. Shopify 後台 → Online Store → Themes → Edit code
 * 2. 在 Assets 資料夾新增檔案 goyoutati-reference-price.js
 * 3. 貼上此程式碼
 * 4. 在 layout/theme.liquid 的 </body> 前加入：
 *    <script src="{{ 'goyoutati-reference-price.js' | asset_url }}" defer></script>
 */

(function() {
  'use strict';

  // ============ 設定區 ============
  const CONFIG = {
    // 國家對應貨幣設定
    countries: {
      'CA': { code: 'CAD', symbol: 'CA$' },
      'HK': { code: 'HKD', symbol: 'HK$' },
      'JP': { code: 'JPY', symbol: '¥', skip: true }, // 日本不需要顯示
      'SG': { code: 'SGD', symbol: 'S$' },
      'TW': { code: 'TWD', symbol: 'NT$' },
      'US': { code: 'USD', symbol: 'US$' }
    },

    // 匯率：一律讀 daigo 後端 /api/rate（2026-10-11 起全站單一來源）。
    // 回的是加成後的參考匯率（加成率、幣別由後端 REFERENCE_RATE_MARKUP／REFERENCE_CURRENCIES 決定）；回應裡沒有的幣別不顯示。
    apiUrl: 'https://goyoutatidaigo.zeabur.app/api/rate',

    // 快取時間（1小時）
    cacheTime: 60 * 60 * 1000,

    // 快取 key：全站共用同一份（daigo.js、首頁商品、計算機、活動頁都用這個 key、同一個格式 {rates, ts}），
    // 一個訪客一個 session 只打一次 /api/rate。舊 key goyoutati_rates 存的是 v4 純市價，不再讀。
    storageKey: 'gyt_ref_rates_v1',

    // 參考價格樣式
    refPriceClass: 'gyt-ref-price',

    // 多語言文字
    i18n: {
      en: 'Approx.',
      zh: '參考價格'
    },

    // 除錯模式
    debug: false
  };

  // ============ 工具函數 ============
  
  function log(...args) {
    if (CONFIG.debug) console.log('[GOYOUTATI]', ...args);
  }

  // 數字格式化（千分位）
  function formatMoney(amount, decimals = 0) {
    return amount.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // 解析日幣金額（從文字中提取數字）
  function parseJPY(text) {
    if (!text) return null;
    // 移除 ¥ 符號、逗號、JPY 文字，保留數字
    const cleaned = text.replace(/[¥￥,，\s]|JPY/gi, '');
    const match = cleaned.match(/[\d.]+/);
    return match ? parseFloat(match[0]) : null;
  }

  // ============ 匯率管理 ============

  // 同一頁只抓一次：updateAllPrices 會被 DOM 變化反覆觸發，失敗時不可以每次重打
  let _ratesPromise = null;

  function getRates() {
    if (!_ratesPromise) _ratesPromise = fetchRates();
    return _ratesPromise;
  }

  async function fetchRates() {
    // 檢查快取
    try {
      const data = JSON.parse(sessionStorage.getItem(CONFIG.storageKey) || 'null');
      if (data && data.rates && Date.now() - data.ts < CONFIG.cacheTime) {
        log('使用快取匯率', data.rates);
        return data.rates;
      }
    } catch (e) {}

    // 從 API 獲取
    try {
      log('從 API 獲取匯率...');
      const response = await fetch(CONFIG.apiUrl);
      const data = await response.json();
      // 新格式 { rates: {TWD, HKD} }；舊格式只有 { jpy_to_twd }（後端先上線前的相容）
      const rates = data.rates || (data.jpy_to_twd ? { TWD: data.jpy_to_twd } : {});
      if (Object.keys(rates).length) {
        try {
          sessionStorage.setItem(CONFIG.storageKey, JSON.stringify({ rates: rates, ts: Date.now() }));
        } catch (e) {}
      }
      log('匯率已更新', rates);
      return rates;
    } catch (error) {
      // 抓不到就不顯示參考價（不再用寫死的備用匯率）
      console.error('[GOYOUTATI] 無法獲取匯率:', error);
      return {};
    }
  }

  // ============ 國家偵測 ============

  function getCurrentLanguage() {
    // 從 HTML lang 屬性判斷
    const lang = document.documentElement.lang || '';
    if (lang.startsWith('zh')) return 'zh';
    if (lang.startsWith('en')) return 'en';
    
    // 從 Shopify locale 判斷
    if (window.Shopify && window.Shopify.locale) {
      if (window.Shopify.locale.startsWith('zh')) return 'zh';
      return 'en';
    }
    
    // 預設英文
    return 'en';
  }

  function getCurrentCountry() {
    // 方法1：從 Shopify localization 對象
    if (window.Shopify && window.Shopify.country) {
      return window.Shopify.country;
    }

    // 方法2：從頁面上的國家選擇器文字判斷
    const localizationForm = document.querySelector('localization-form');
    if (localizationForm) {
      const button = localizationForm.querySelector('button[aria-expanded]');
      if (button) {
        const text = button.textContent || '';
        if (text.includes('Canada')) return 'CA';
        if (text.includes('Hong Kong')) return 'HK';
        if (text.includes('Japan')) return 'JP';
        if (text.includes('Taiwan')) return 'TW';
        if (text.includes('United States')) return 'US';
      }
    }

    // 方法3：從 URL 路徑
    const pathMatch = window.location.pathname.match(/^\/([a-z]{2})(?:-[a-z]{2})?\//i);
    if (pathMatch) {
      const code = pathMatch[1].toUpperCase();
      if (CONFIG.countries[code]) return code;
    }

    // 方法4：從 HTML lang 屬性猜測
    const lang = document.documentElement.lang;
    if (lang) {
      if (lang.includes('zh-TW')) return 'TW';
      if (lang.includes('zh-HK')) return 'HK';
    }

    // 預設
    return 'HK';
  }

  // ============ 價格轉換 ============

  function convertPrice(jpyAmount, rates, countryCode) {
    const country = CONFIG.countries[countryCode];
    if (!country || country.skip) return null;

    const rate = rates[country.code];
    if (!rate) return null;

    const converted = jpyAmount * rate;
    const formatted = formatMoney(converted, country.code === 'USD' || country.code === 'CAD' ? 2 : 0);
    
    return `${country.symbol} ${formatted}`;
  }

  // ============ DOM 操作 ============

  // 移除所有已添加的參考價格
  function clearRefPrices() {
    document.querySelectorAll(`.${CONFIG.refPriceClass}`).forEach(el => el.remove());
  }

  // 添加參考價格到價格元素
  function addRefPrice(priceElement, refPriceText) {
    // 檢查是否已經添加過
    if (priceElement.querySelector(`.${CONFIG.refPriceClass}`)) return;
    if (priceElement.nextElementSibling?.classList.contains(CONFIG.refPriceClass)) return;

    const lang = getCurrentLanguage();
    const label = CONFIG.i18n[lang] || CONFIG.i18n.en;

    const refSpan = document.createElement('span');
    refSpan.className = CONFIG.refPriceClass;
    refSpan.style.cssText = `
      display: block;
      font-size: 0.85em;
      color: #666;
      margin-top: 2px;
    `;
    refSpan.textContent = `（${label} ${refPriceText}）`;

    // 插入到價格元素後面
    if (priceElement.parentNode) {
      priceElement.parentNode.insertBefore(refSpan, priceElement.nextSibling);
    }
  }

  // ============ 主要更新邏輯 ============

  async function updateAllPrices() {
    const countryCode = getCurrentCountry();
    log('當前國家:', countryCode);

    // 如果是日本，不需要顯示參考價格
    if (CONFIG.countries[countryCode]?.skip) {
      clearRefPrices();
      return;
    }

    const rates = await getRates();
    
    // Dawn 主題的價格選擇器
    const priceSelectors = [
      // 商品卡片價格
      '.price-item--regular',
      '.price-item--sale',
      '.price__regular .price-item',
      '.price__sale .price-item',
      // 商品詳情頁價格
      '.price__container .price-item',
      '.product__price .price-item',
      // 購物車價格
      '.cart-item__price .price',
      '.cart-item__discounted-prices .price',
      '.cart-item__old-price .price',
      // 購物車總計
      '.totals__subtotal-value',
      '.totals__total-value',
      // 側邊購物車（Drawer）
      '.cart-drawer-item__price .price',
      '.cart-drawer__subtotal .price'
    ];

    // 處理所有價格元素
    priceSelectors.forEach(selector => {
      document.querySelectorAll(selector).forEach(el => {
        // ★ 跳過被折扣腳本處理過的元素（折扣腳本產生的原價刪除線和折扣價）
        if (el.dataset.gytDiscounted) return;
        if (el.closest('[data-gyt-discounted]')) return;
        if (el.classList.contains('gyt-discount-original') || el.classList.contains('gyt-discount-sale')) return;

        // ★ 如果元素內有折扣腳本的子元素，從原價元素讀取金額
        const discountOriginal = el.querySelector('.gyt-discount-original');
        let jpyAmount;
        if (discountOriginal) {
          // 從刪除線的原價文字中讀取金額
          jpyAmount = parseJPY(discountOriginal.textContent);
        } else {
          jpyAmount = parseJPY(el.textContent);
        }

        if (jpyAmount) {
          const refPrice = convertPrice(jpyAmount, rates, countryCode);
          if (refPrice) {
            addRefPrice(el, refPrice);
          }
        }
      });
    });

  }

  // ============ 事件監聽 ============

  function setupListeners() {
    // 監聽國家切換（localization form 變化）
    document.addEventListener('change', (e) => {
      if (e.target.closest('localization-form')) {
        log('國家切換，重新計算...');
        setTimeout(updateAllPrices, 500);
      }
    });

    // 監聽購物車更新（Shopify 的 cart 事件）
    document.addEventListener('cart:updated', updateAllPrices);
    document.addEventListener('cart:refresh', updateAllPrices);

    // 監聽 AJAX 請求完成（購物車操作）
    const originalFetch = window.fetch;
    window.fetch = async function(...args) {
      const response = await originalFetch.apply(this, args);
      const url = args[0]?.toString() || '';
      if (url.includes('/cart')) {
        setTimeout(updateAllPrices, 300);
      }
      return response;
    };

    // 監聽 DOM 變化（側邊購物車開啟、動態載入商品等）
    // ★ 忽略折扣腳本產生的 DOM 變化
    let _refPriceDebounce = null;
    const observer = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          // 忽略折扣腳本產生的元素
          if (node.dataset?.gytDiscounted) continue;
          if (typeof node.className === 'string' && node.className.indexOf('gyt-discount') !== -1) continue;
          // 忽略折扣腳本的參考價格包裝
          if (node.classList?.contains('gyt-discount-ref-wrap')) continue;
          
          // 檢查是否有新的價格元素
          if (node.classList?.contains('price-item') ||
              node.classList?.contains('price') ||
              node.classList?.contains('card-information') ||
              node.classList?.contains('cart-drawer') ||
              node.querySelector?.('.price-item, .card-information, .cart-item, .cart-drawer-item')) {
            shouldUpdate = true;
            break;
          }
        }
        if (shouldUpdate) break;
      }
      
      if (shouldUpdate) {
        clearTimeout(_refPriceDebounce);
        _refPriceDebounce = setTimeout(updateAllPrices, 200);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  // ============ 初始化 ============

  function init() {
    log('初始化參考價格腳本...');
    
    // 添加 CSS
    const style = document.createElement('style');
    style.textContent = `
      .${CONFIG.refPriceClass} {
        display: block;
        font-size: 0.85em;
        color: #666;
        margin-top: 2px;
      }
    `;
    document.head.appendChild(style);

    // 首次更新
    updateAllPrices();

    // 設置監聽器
    setupListeners();

    log('初始化完成');
  }

  // 等待 DOM 載入完成
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
